import Page from "@/components/layout/Page";
import * as Styled from "./styled";
import Card from "@/components/shared-ui/Card";
import { CardHeading } from "../AdminDashboard/styled";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import useApi from "@/contexts/ApiContext";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import Input from "@/components/shared-ui/Input";
import { useEffect, useState } from "react";
import type { Auth0User } from "@/services/api/types";
import Select from "@/components/shared-ui/Select";
import Button from "@/components/shared-ui/Button";
import Modal from "@/components/shared-ui/Modal";
import MultiSelectDropdown from "@/components/shared-ui/MultiSelectDropdown";
import { AxiosError } from "axios";
import { sortCompanies } from "../admin.utils";

const AdminUserEdit = () => {
  const { refresh, ...api } = useApi();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [userUpdate, setUserUpdate] = useState<Auth0User>();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data: user, isSuccess } = useQuery({ queryKey: ["get-user", userId], queryFn: () =>
    api.admin.user.get(userId || "")
  });
  const { data: companies } = useQuery({ queryKey: ["get-companies"], queryFn: () =>
    api.admin.company.getAll()
  });
  const { data: roles } = useQuery({ queryKey: ["get-roles"], queryFn: () =>
    api.admin.roles.getAll()
  });

  const updateUser = useMutation({
    mutationFn: api.admin.user.update,
    onSettled: () => {
      setSubmitted(true);
    },
  });

  const onClose = () => {
    setModalOpen(false);
    setSubmitted(false);
    // Refresh user data via ApiContext to trigger a rerender
    refresh();
    if (updateUser.isSuccess) {
      navigate("/admin/users");
    }
  };

  useEffect(() => {
    if (user && user.roles && isSuccess) {
      const { roles, ...userWithoutRoles } = user;
      setUserUpdate(userWithoutRoles);
      setUserRoles(roles.map((role) => role.id));
    }
  }, [user, isSuccess]);

  if (!user || !userUpdate || !companies || !roles) {
    return <LoadingScreen isAdmin />;
  }

  const companyOptions = sortCompanies(companies);

  const roleOptions = [
    ...roles.map((role) => ({
      value: role.id,
      label: (
        <div>
          <strong>{`${role.name} - `}</strong>
          <small>{role.description}</small>
        </div>
      ),
    })),
    {
      disabled: true,
      value: "",
      label: (
        <div>
          <strong>{`basic - `}</strong>
          <small>Default role assigned to all users. Limited features.</small>
        </div>
      ),
    },
  ];

  const onSubmit = () => {
    setModalOpen(true);
    return false;
  };

  const displayError = () => {
    const errorData =
      updateUser.error instanceof AxiosError
        ? updateUser.error.response?.data
        : undefined;

    if (errorData === "You may not assign this user to the admin role") {
      return (
        <>
          <p>User updated but could not assign roles to user.</p>{" "}
          <p>
            Ensure the user has a Seccl email address if trying to assign the
            admin role.
          </p>
        </>
      );
    }

    if (
      errorData ===
      "This company can only be assigned to users with a seccl.tech email address"
    ) {
      return <p>{errorData}.</p>;
    }

    return "Sorry there was an issue updating the user.";
  };

  return (
    <Page
      title="User edit"
      isAdmin
      customBreadcrumb={{ path: "/admin/users", label: "Back to users" }}
    >
      <Card>
        <CardHeading>Edit user</CardHeading>
        <Styled.Form>
          <Input
            label="Name"
            id="name"
            name="name"
            type="text"
            value={userUpdate.name}
            onChange={(e) =>
              setUserUpdate({
                ...userUpdate,
                name: e.target.value,
              })
            }
            data-testid="userNameFieldEdit"
          />
          <Input
            label="Email"
            id="email"
            name="email"
            type="email"
            disabled
            value={userUpdate.email}
          />

          <Select
            id="company"
            name="company"
            label="Company"
            value={userUpdate.app_metadata.companyId}
            onChange={(e) =>
              setUserUpdate({
                ...userUpdate,
                app_metadata: {
                  ...userUpdate.app_metadata,
                  companyId: e.target.value,
                },
              })
            }
            options={companyOptions}
            data-testid="userCompanyFieldEdit"
          />
          <MultiSelectDropdown
            id="roles"
            label="Roles"
            options={roleOptions}
            values={userRoles}
            onValueChange={(v) => setUserRoles(v)}
            customTitleText={`${userRoles.length + 1} active`}
            data-testid="userRoleFieldEdit"
          />
          <Styled.ButtonWrapper>
            <Button
              type="button"
              label="Submit changes"
              disabled={modalOpen}
              data-testid="editUserSubmit"
              onClick={onSubmit}
            />
          </Styled.ButtonWrapper>
        </Styled.Form>
      </Card>
      <Modal
        modalActive={modalOpen && !submitted}
        toggleModal={() => setModalOpen((prev) => !prev)}
        title="Are you sure?"
        actionButton={{
          label: "Save updates",
          onClick: () => {
            updateUser.mutate({
              user: userUpdate,
              rolesToAdd: userRoles.filter(
                (updatedRole) =>
                  !user.roles?.find((role) => role.id === updatedRole)
              ),
              rolesToDelete: user.roles
                ? user.roles
                    .filter((role) => !userRoles.includes(role.id))
                    .map((role) => role.id)
                : [],
            });
          },
          "data-testid": "confirmationModalButton",
        }}
        cancelButton={{ label: "Cancel", onClick: () => setModalOpen(false) }}
      >
        <p>This will change the user details.</p>
        Ensure you have checked that everything is correct before submitting
        (especially company).
      </Modal>
      <Modal
        modalActive={modalOpen && submitted}
        toggleModal={() => setModalOpen((prev) => !prev)}
        title={updateUser.isSuccess ? "Successfully updated" : "Error"}
        cancelButton={{
          label: "Close",
          onClick: onClose,
          "data-testid": "completionModalButton",
        }}
        disableOutsideClick={updateUser.isSuccess}
      >
        {updateUser.isSuccess && "The user has been successfully updated."}
        {updateUser.isError && displayError()}
      </Modal>
    </Page>
  );
};

export default AdminUserEdit;
